"use client";

import { useCallback, useEffect, useRef } from "react";

type TurnstileRenderOpts = {
  sitekey: string;
  size?: "normal" | "compact" | "invisible";
  execution?: "render" | "execute";
  callback?: (token: string) => void;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
};

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement | string, opts: TurnstileRenderOpts) => string;
      execute: (id: string) => void;
      reset: (id: string) => void;
      remove: (id: string) => void;
    };
  }
}

// Stable id shared by server and client render. A random per-instance id
// hydration-mismatches: the SSR DOM keeps the server's random id while the
// client hook polls forever for its own, so the widget never renders and
// every request goes out tokenless.
const TURNSTILE_CONTAINER_ID = "cf-turnstile-host";

// How long getToken() will wait for the Cloudflare script + widget to become ready before
// giving up. The script loads async; on a slow (mobile) connection a user can paste a URL
// and submit before it arrives. Returning "" instantly in that case sent a tokenless
// request that was guaranteed a 403 — the single largest source of "Verification failed".
const READY_WAIT_MS = 8_000;

function waitFor(predicate: () => boolean, timeoutMs: number): Promise<boolean> {
  if (predicate()) return Promise.resolve(true);
  return new Promise((resolve) => {
    const started = Date.now();
    const interval = setInterval(() => {
      if (predicate()) {
        clearInterval(interval);
        resolve(true);
      } else if (Date.now() - started >= timeoutMs) {
        clearInterval(interval);
        resolve(false);
      }
    }, 100);
  });
}

export function useTurnstile() {
  const widgetIdRef = useRef<string | null>(null);
  const resolverRef = useRef<((token: string) => void) | null>(null);
  const containerIdRef = useRef<string>(TURNSTILE_CONTAINER_ID);

  useEffect(() => {
    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
    if (!siteKey) return;

    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;

    const tryRender = () => {
      if (cancelled || widgetIdRef.current) return;
      if (!window.turnstile) return;
      const el = document.getElementById(containerIdRef.current);
      if (!el) return;

      widgetIdRef.current = window.turnstile.render(el, {
        sitekey: siteKey,
        size: "invisible",
        execution: "execute",
        callback: (token: string) => {
          const r = resolverRef.current;
          resolverRef.current = null;
          r?.(token);
        },
        "error-callback": () => {
          const r = resolverRef.current;
          resolverRef.current = null;
          r?.("");
        },
        "expired-callback": () => {
          if (window.turnstile && widgetIdRef.current) {
            try { window.turnstile.reset(widgetIdRef.current); } catch {}
          }
        },
      });
    };

    if (window.turnstile) {
      tryRender();
    } else {
      interval = setInterval(() => {
        if (window.turnstile) {
          if (interval) { clearInterval(interval); interval = null; }
          tryRender();
        }
      }, 100);
    }

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      if (window.turnstile && widgetIdRef.current) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
        widgetIdRef.current = null;
      }
    };
  }, []);

  const getToken = useCallback(async (): Promise<string> => {
    if (!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) return "";
    if (typeof window === "undefined") return "";
    if (!window.turnstile || !widgetIdRef.current) {
      const ready = await waitFor(() => !!window.turnstile && !!widgetIdRef.current, READY_WAIT_MS);
      if (!ready) return "";
    }

    return new Promise<string>((resolve) => {
      const timer = setTimeout(() => {
        if (resolverRef.current) {
          resolverRef.current = null;
          resolve("");
        }
      }, 10_000);

      resolverRef.current = (token: string) => {
        clearTimeout(timer);
        resolve(token);
      };

      try {
        window.turnstile!.reset(widgetIdRef.current!);
        window.turnstile!.execute(widgetIdRef.current!);
      } catch {
        clearTimeout(timer);
        resolverRef.current = null;
        resolve("");
      }
    });
  }, []);

  return { containerId: containerIdRef.current, getToken };
}
